import { motion } from "framer-motion";
import { PartyPopper, CalendarDays, X, Sparkles, MessageCircle } from "lucide-react";
import { FESTIVAL_DATES, FESTIVAL_MONTH } from "@/lib/festival";
import { useEffect, useMemo } from "react";

const COLORS = ["#FFD600", "#E63946", "#0066CC", "#10B981", "#F97316"];

function buildWhatsAppUrl(data) {
  // Normalize phone: keep digits only, prepend country code 218 if missing
  const raw = (data?.companion_phone || "").replace(/\D/g, "");
  let phone = raw;
  if (!phone) return null;
  if (phone.startsWith("00")) phone = phone.slice(2);
  if (phone.startsWith("0")) phone = "218" + phone.slice(1);
  if (!phone.startsWith("218") && phone.length <= 10) phone = "218" + phone;

  const kidsList = [...(data.competitions || []), ...(data.workshops || [])].join("، ");
  const feeLine = data.fee != null ? `💰 الرسوم: ${data.fee} د.ل\n` : "";
  const msg =
    `🎉 تهانينا! تم تسجيل الطفل بنجاح في مهرجان العودة للمدارس - ليبيا\n\n` +
    `👦 الاسم: ${data.full_name}\n` +
    `🏫 المدرسة: ${data.school_name}\n` +
    `📅 التواريخ: 17 • 18 • 19 سبتمبر\n` +
    `⏰ الوقت: من 5 مساءً إلى 8 مساءً\n` +
    `📍 المكان: منتزه الميريلاند - تاجوراء\n` +
    (kidsList ? `🎯 الأنشطة: ${kidsList}\n` : "") +
    feeLine +
    `🆔 رقم التسجيل: ${(data.id || "").slice(0, 8)}\n\n` +
    `نراك في المهرجان! ✨`;
  return `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`;
}

export default function SuccessScreen({ data, onClose }) {
  const waUrl = useMemo(() => buildWhatsAppUrl(data), [data]);

  // Auto-open WhatsApp link in new tab shortly after success screen renders
  useEffect(() => {
    if (!waUrl) return;
    const t = setTimeout(() => {
      try { window.open(waUrl, "_blank", "noopener,noreferrer"); } catch (e) { /* ignore */ }
    }, 1200);
    return () => clearTimeout(t);
  }, [waUrl]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      data-testid="success-screen"
    >
      <div className="absolute inset-0 bg-[#1E293B]/70 backdrop-blur-sm" onClick={onClose} />
      {/* Confetti */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {[...Array(28)].map((_, i) => (
          <motion.span
            key={i}
            className="confetti-dot"
            style={{
              left: `${(i * 37) % 100}%`,
              backgroundColor: COLORS[i % COLORS.length],
            }}
            initial={{ y: -40, rotate: 0, opacity: 1 }}
            animate={{ y: "110vh", rotate: 360 * (i % 2 === 0 ? 1 : -1) }}
            transition={{ duration: 3 + (i % 5) * 0.4, delay: i * 0.05, ease: "easeIn" }}
          />
        ))}
      </div>

      <motion.div
        initial={{ scale: 0.85, y: 30, opacity: 0 }}
        animate={{ scale: 1, y: 0, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ type: "spring", stiffness: 180, damping: 20 }}
        className="relative bg-white rounded-3xl shadow-2xl max-w-lg w-full p-8 sm:p-10 border-4 border-[#FFD600]"
      >
        <button
          onClick={onClose}
          className="absolute top-4 left-4 w-9 h-9 rounded-full bg-[#FFFDF0] hover:bg-[#FFD600] flex items-center justify-center transition-colors"
          data-testid="success-close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="text-center">
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            className="inline-flex w-20 h-20 rounded-full bg-gradient-to-br from-[#FFD600] to-[#E63946] items-center justify-center shadow-xl"
          >
            <PartyPopper className="w-10 h-10 text-white" />
          </motion.div>

          <h2 className="font-display text-3xl sm:text-4xl font-black text-[#1E293B] mt-6" data-testid="success-title">
            شكرًا لتسجيل طفلك!
          </h2>
          <p className="text-[#475569] font-semibold mt-2">
            تم تسجيل <span className="text-[#0066CC] font-black">{data?.full_name}</span> بنجاح.
            سيصلك تأكيد عبر رقم هاتف المرافق قريبًا.
          </p>

          {data?.fee != null && (
            <div className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#FFD600] text-[#1E293B] font-black shadow-md" data-testid="success-fee">
              رسوم التسجيل: {data.fee} د.ل
            </div>
          )}

          {/* Dates card */}
          <div className="mt-8 p-5 rounded-2xl bg-gradient-to-br from-[#0066CC] to-[#1E293B] text-white" data-testid="success-dates">
            <div className="flex items-center justify-center gap-2 text-[#FFD600] font-black text-sm">
              <CalendarDays className="w-4 h-4" />
              نراك في المهرجان
            </div>
            <div className="mt-3 flex items-center justify-center gap-3">
              {FESTIVAL_DATES.map((d, i) => (
                <motion.div
                  key={d}
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.4 + i * 0.1 }}
                  className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-white text-[#0066CC] flex flex-col items-center justify-center shadow-lg"
                >
                  <span className="font-display text-2xl sm:text-3xl font-black leading-none">{d}</span>
                  <span className="text-[10px] font-bold mt-1">{FESTIVAL_MONTH}</span>
                </motion.div>
              ))}
            </div>
            <p className="mt-4 text-sm font-semibold opacity-90">
              يوميًا من 5 مساءً إلى 8 مساءً — نراك هناك!
            </p>
          </div>

          <div className="mt-6 inline-flex items-center gap-2 text-xs font-black text-[#0066CC]">
            <Sparkles className="w-3.5 h-3.5" />
            رقم التسجيل: <span dir="ltr" className="font-mono">{data?.id?.slice(0, 8)}</span>
          </div>
        </div>

        {waUrl && (
          <a
            href={waUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-6 w-full inline-flex items-center justify-center gap-2 py-3.5 px-5 rounded-full bg-[#25D366] hover:bg-[#1ea952] text-white font-black shadow-lg transition-colors"
            data-testid="success-whatsapp"
          >
            <MessageCircle className="w-5 h-5" />
            إرسال التأكيد عبر واتساب
          </a>
        )}

        <button
          onClick={onClose}
          className="mt-3 w-full btn-3d justify-center"
          data-testid="success-done"
        >
          تمّ — إغلاق
        </button>
      </motion.div>
    </motion.div>
  );
}
